import { useState } from 'react';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { fetchStrategyConfig, previewDecision, saveStrategyConfig } from '../api/endpoints';
import { errorMessageRu, fieldErrorsRu } from '../api/errors';
import { queryKeys } from '../api/query-keys';
import { SmoothingTypeSchema } from '../api/schemas';
import type { PreviewResponse, StrategyConfig } from '../api/schemas';
import { useSession } from '../auth/session-context';
import { withReauth } from '../auth/with-reauth';
import { Banner } from '../components/Banner';
import { Field } from '../components/Field';
import { KeyValue } from '../components/KeyValue';
import { Price, Quantity, Usdt } from '../components/Money';
import { QueryState } from '../components/QueryState';
import { ReasonText } from '../components/ReasonText';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import { DECISION_LABEL, DECISION_TONE } from '../domain/labels';
import { newIdempotencyKey } from '../lib/idempotency';
import { decimalString, integerInRange, numberInRange } from '../lib/validation';

/**
 * Раздел 2 — «Стратегия».
 *
 * Reads the config, edits it under a zod schema that mirrors the API contract,
 * and offers «Предпросмотр решения» — a dry run through the real engine that
 * returns what it would decide right now and places no order. Every monetary
 * and percentage field is a text input holding a decimal string; the only
 * `type=number` inputs are counts and periods, which are genuinely integers.
 */

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const StrategyFormSchema = z
  .object({
    strategyVersion: z.string(),
    symbol: z.string().min(1, 'Укажите инструмент.'),
    timezone: z.string().min(1, 'Укажите таймзону, например Europe/Moscow.'),
    decisionTime: z.string().regex(TIME_RE, 'Формат времени ЧЧ:ММ, например 09:00.'),
    entryWindowMinutes: integerInRange(1, 240, 'минуты'),
    rsiPeriod: integerInRange(2, 200, 'свечи'),
    // Reuses the contract's own enum rather than restating it, so the form can
    // never offer fewer smoothing types than the strategy engine supports.
    // `RMA` (Wilder) was missing here and in the contract, which made an RMA
    // configuration unsavable and unrepresentable.
    smoothingType: SmoothingTypeSchema,
    smoothingPeriod: integerInRange(1, 200, 'свечи'),
    overboughtThreshold: numberInRange(50, 100),
    neutralThreshold: numberInRange(0, 100),
    impulseThresholdPct: decimalString({ positive: true, max: '100' }),
    cooldownSessions: integerInRange(0, 60, 'сессии'),
    leverage: integerInRange(1, 125, 'кратность плеча'),
    slRoiPct: decimalString({ positive: true, max: '1000' }),
    tpRoiPct: decimalString({ positive: true, max: '10000' }),
    marginAmount: decimalString({ positive: true }),
  })
  .superRefine((values, ctx) => {
    if (values.neutralThreshold >= values.overboughtThreshold) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['neutralThreshold'],
        message: 'Нейтральный порог должен быть ниже порога перекупленности.',
      });
    }
  });

type StrategyForm = z.infer<typeof StrategyFormSchema>;

export function StrategyPage(): ReactElement {
  const query = useQuery({
    queryKey: queryKeys.strategyConfig,
    queryFn: ({ signal }) => fetchStrategyConfig(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Стратегия</h1>
        <span className="page-head__hint">
          Параметры сигнала, риска и расписания. Изменения вступают в силу со следующего решения.
        </span>
      </div>
      <QueryState query={query} what="параметры стратегии">
        {(config) => <StrategyEditor config={config} />}
      </QueryState>
    </>
  );
}

function StrategyEditor({ config }: { config: StrategyConfig }): ReactElement {
  const queryClient = useQueryClient();
  const { canOperate, promptReauth } = useSession();
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveErrorRu, setSaveErrorRu] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<StrategyForm>({
    resolver: zodResolver(StrategyFormSchema),
    defaultValues: config,
  });

  const preview = useMutation({
    mutationFn: () => previewDecision(getValues()),
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaveErrorRu(null);
    const idempotencyKey = newIdempotencyKey();
    try {
      const saved = await withReauth(
        () => saveStrategyConfig(values, idempotencyKey),
        promptReauth,
      );
      reset(saved);
      setSavedAt(new Date().toISOString());
      await queryClient.invalidateQueries({ queryKey: queryKeys.strategyConfig });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    } catch (error) {
      setSaveErrorRu(errorMessageRu(error));
      for (const [field, message] of Object.entries(fieldErrorsRu(error))) {
        if (field in config) setError(field as keyof StrategyForm, { message });
      }
    }
  });

  return (
    <div className="stack">
      {!canOperate && (
        <Banner tone="warn" title="Только просмотр.">
          Ваша роль не позволяет изменять параметры стратегии. Значения показаны для контроля.
        </Banner>
      )}

      <form onSubmit={onSubmit} noValidate>
        <div className="stack">
          <Section
            title="Расписание"
            hint="Когда движок принимает решение и как долго действует окно входа."
          >
            <div className="form-grid">
              <Field
                id="cfg-symbol"
                label="Инструмент"
                hint="Торговый символ перпетуального контракта."
                error={errors.symbol?.message}
              >
                <input id="cfg-symbol" type="text" disabled {...register('symbol')} />
              </Field>

              <Field
                id="cfg-timezone"
                label="Таймзона стратегии"
                hint="Локальная торговая дата считается в этой зоне (IANA, например Europe/Moscow)."
                error={errors.timezone?.message}
              >
                <input
                  id="cfg-timezone"
                  type="text"
                  disabled={!canOperate}
                  aria-invalid={errors.timezone !== undefined}
                  aria-describedby="cfg-timezone-hint"
                  {...register('timezone')}
                />
              </Field>

              <Field
                id="cfg-decision-time"
                label="Время решения"
                hint="Часы и минуты в таймзоне стратегии, формат ЧЧ:ММ."
                error={errors.decisionTime?.message}
              >
                <input
                  id="cfg-decision-time"
                  type="text"
                  inputMode="numeric"
                  placeholder="09:00"
                  disabled={!canOperate}
                  aria-invalid={errors.decisionTime !== undefined}
                  aria-describedby="cfg-decision-time-hint"
                  {...register('decisionTime')}
                />
              </Field>

              <Field
                id="cfg-window"
                label="Окно входа"
                hint="Минуты после времени решения. Пропущенное окно не догоняется."
                error={errors.entryWindowMinutes?.message}
              >
                <input
                  id="cfg-window"
                  type="number"
                  min={1}
                  max={240}
                  step={1}
                  disabled={!canOperate}
                  aria-invalid={errors.entryWindowMinutes !== undefined}
                  aria-describedby="cfg-window-hint"
                  {...register('entryWindowMinutes', { valueAsNumber: true })}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Индикаторы"
            hint="RSI и сглаживающая линия, рассчитанные по закрытым свечам."
          >
            <div className="form-grid">
              <Field
                id="cfg-rsi-period"
                label="Период RSI"
                hint="Количество закрытых свечей."
                error={errors.rsiPeriod?.message}
              >
                <input
                  id="cfg-rsi-period"
                  type="number"
                  min={2}
                  max={200}
                  step={1}
                  disabled={!canOperate}
                  aria-invalid={errors.rsiPeriod !== undefined}
                  aria-describedby="cfg-rsi-period-hint"
                  {...register('rsiPeriod', { valueAsNumber: true })}
                />
              </Field>

              <Field
                id="cfg-smoothing-type"
                label="Тип сглаживания"
                hint="Скользящая средняя, применяемая к линии RSI."
                error={errors.smoothingType?.message}
              >
                <select
                  id="cfg-smoothing-type"
                  disabled={!canOperate}
                  aria-describedby="cfg-smoothing-type-hint"
                  {...register('smoothingType')}
                >
                  <option value="SMA">Простая (SMA)</option>
                  <option value="EMA">Экспоненциальная (EMA)</option>
                  <option value="RMA">Сглаженная по Уайлдеру (RMA)</option>
                </select>
              </Field>

              <Field
                id="cfg-smoothing-period"
                label="Период сглаживания"
                hint="Количество значений RSI в скользящей средней."
                error={errors.smoothingPeriod?.message}
              >
                <input
                  id="cfg-smoothing-period"
                  type="number"
                  min={1}
                  max={200}
                  step={1}
                  disabled={!canOperate}
                  aria-invalid={errors.smoothingPeriod !== undefined}
                  aria-describedby="cfg-smoothing-period-hint"
                  {...register('smoothingPeriod', { valueAsNumber: true })}
                />
              </Field>

              <Field
                id="cfg-overbought"
                label="Порог перекупленности"
                hint="Значение RSI, выше которого вход выполняется в шорт."
                error={errors.overboughtThreshold?.message}
              >
                <input
                  id="cfg-overbought"
                  type="number"
                  min={50}
                  max={100}
                  step="0.1"
                  disabled={!canOperate}
                  aria-invalid={errors.overboughtThreshold !== undefined}
                  aria-describedby="cfg-overbought-hint"
                  {...register('overboughtThreshold', { valueAsNumber: true })}
                />
              </Field>

              <Field
                id="cfg-neutral"
                label="Нейтральный порог"
                hint="Граница между восходящим и нисходящим режимом."
                error={errors.neutralThreshold?.message}
              >
                <input
                  id="cfg-neutral"
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  disabled={!canOperate}
                  aria-invalid={errors.neutralThreshold !== undefined}
                  aria-describedby="cfg-neutral-hint"
                  {...register('neutralThreshold', { valueAsNumber: true })}
                />
              </Field>
            </div>
          </Section>

          <Section title="Импульс и пауза" hint="Защита от входа сразу после резкого движения.">
            <div className="form-grid">
              <Field
                id="cfg-impulse"
                label="Порог импульса, %"
                hint="Рост от открытия дня, после которого входы приостанавливаются. Десятичное число строкой."
                error={errors.impulseThresholdPct?.message}
              >
                <input
                  id="cfg-impulse"
                  type="text"
                  inputMode="decimal"
                  disabled={!canOperate}
                  aria-invalid={errors.impulseThresholdPct !== undefined}
                  aria-describedby="cfg-impulse-hint"
                  {...register('impulseThresholdPct')}
                />
              </Field>

              <Field
                id="cfg-cooldown"
                label="Сессий паузы"
                hint="Сколько плановых решений пропускается после импульса."
                error={errors.cooldownSessions?.message}
              >
                <input
                  id="cfg-cooldown"
                  type="number"
                  min={0}
                  max={60}
                  step={1}
                  disabled={!canOperate}
                  aria-invalid={errors.cooldownSessions !== undefined}
                  aria-describedby="cfg-cooldown-hint"
                  {...register('cooldownSessions', { valueAsNumber: true })}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Риск и размер позиции"
            hint="Денежные значения вводятся строкой, без округления до числа с плавающей точкой."
          >
            <div className="form-grid">
              <Field
                id="cfg-margin"
                label="Маржа на сделку, USDT"
                hint="Сумма собственных средств в сделке. Валюта — USDT."
                error={errors.marginAmount?.message}
              >
                <input
                  id="cfg-margin"
                  type="text"
                  inputMode="decimal"
                  disabled={!canOperate}
                  aria-invalid={errors.marginAmount !== undefined}
                  aria-describedby="cfg-margin-hint"
                  {...register('marginAmount')}
                />
              </Field>

              <Field
                id="cfg-leverage"
                label="Плечо"
                hint="Кратность. Площадка должна разрешать это значение, подмена не допускается."
                error={errors.leverage?.message}
              >
                <input
                  id="cfg-leverage"
                  type="number"
                  min={1}
                  max={125}
                  step={1}
                  disabled={!canOperate}
                  aria-invalid={errors.leverage !== undefined}
                  aria-describedby="cfg-leverage-hint"
                  {...register('leverage', { valueAsNumber: true })}
                />
              </Field>

              <Field
                id="cfg-sl"
                label="Stop Loss, % ROI"
                hint="Процент от маржи, а не от цены. Десятичное число строкой."
                error={errors.slRoiPct?.message}
              >
                <input
                  id="cfg-sl"
                  type="text"
                  inputMode="decimal"
                  disabled={!canOperate}
                  aria-invalid={errors.slRoiPct !== undefined}
                  aria-describedby="cfg-sl-hint"
                  {...register('slRoiPct')}
                />
              </Field>

              <Field
                id="cfg-tp"
                label="Take Profit, % ROI"
                hint="Процент от маржи, а не от цены. Десятичное число строкой."
                error={errors.tpRoiPct?.message}
              >
                <input
                  id="cfg-tp"
                  type="text"
                  inputMode="decimal"
                  disabled={!canOperate}
                  aria-invalid={errors.tpRoiPct !== undefined}
                  aria-describedby="cfg-tp-hint"
                  {...register('tpRoiPct')}
                />
              </Field>
            </div>
          </Section>

          <Section title="Сохранение и предпросмотр">
            <div className="stack stack--tight">
              <KeyValue
                rows={[
                  { label: 'Версия стратегии', value: config.strategyVersion },
                  {
                    label: 'Несохранённые изменения',
                    value: isDirty ? 'да' : 'нет',
                  },
                ]}
              />

              {saveErrorRu !== null && (
                <Banner tone="danger" title="Не сохранено.">
                  {saveErrorRu}
                </Banner>
              )}

              {savedAt !== null && saveErrorRu === null && (
                <Banner tone="ok" title="Параметры сохранены.">
                  Новые значения применяются начиная со следующего планового решения.
                </Banner>
              )}

              <div className="btn-row">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={!canOperate || isSubmitting}
                >
                  {isSubmitting ? 'Сохранение…' : 'Сохранить параметры'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    reset(config);
                    setSaveErrorRu(null);
                  }}
                  disabled={isSubmitting}
                >
                  Отменить изменения
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    preview.mutate();
                  }}
                  disabled={preview.isPending}
                >
                  {preview.isPending ? 'Расчёт…' : 'Предпросмотр решения'}
                </button>
              </div>
              <p className="field__hint">
                Предпросмотр прогоняет текущие значения формы через движок и показывает решение.
                Сделка не создаётся, ордера не отправляются.
              </p>
            </div>
          </Section>
        </div>
      </form>

      {preview.isError && (
        <Banner tone="danger" title="Предпросмотр не выполнен.">
          {errorMessageRu(preview.error)}
        </Banner>
      )}

      {preview.data !== undefined && <PreviewResult preview={preview.data} />}
    </div>
  );
}

function PreviewResult({ preview }: { preview: PreviewResponse }): ReactElement {
  return (
    <Section title="Результат предпросмотра">
      <div className="stack stack--tight">
        <Banner tone="info" title="Расчёт без сделки.">
          Показано решение движка на текущих данных. Ни один ордер не отправлен.
        </Banner>

        <div className="row">
          <StatusPill tone={DECISION_TONE[preview.decision]} srPrefix="Решение движка">
            {DECISION_LABEL[preview.decision]}
          </StatusPill>
        </div>

        <p>
          <ReasonText code={preview.reasonCode} />
        </p>

        <KeyValue
          rows={[
            { label: 'RSI', value: preview.rsi ?? '—' },
            { label: 'Сглаженный RSI', value: preview.smoothedRsi ?? '—' },
            { label: 'Оценка цены входа', value: <Price value={preview.entryPriceEstimate} /> },
            { label: 'Stop Loss', value: <Price value={preview.stopPrice} /> },
            { label: 'Take Profit', value: <Price value={preview.takePrice} /> },
            {
              label: 'Оценка цены ликвидации',
              value: <Price value={preview.liquidationPriceEstimate} />,
            },
            { label: 'Объём', value: <Quantity value={preview.quantity} /> },
            { label: 'Номинал', value: <Usdt value={preview.notional} /> },
            { label: 'Оценка комиссий', value: <Usdt value={preview.estimatedFees} /> },
          ]}
        />
      </div>
    </Section>
  );
}
